import { AppRouteRecordRaw } from '../types';

const Body = () => import('@/components/body.vue');
const PartnerAdministrationOverview = () =>
  import('@/pages/partner-administration/overview/PartnerAdministrationOverview.vue');
const PartnerAdministrationCreate = () =>
  import('@/pages/partner-administration/partner-management/create/PartnerAdministrationCreate.vue');
const PartnerAdministrationList = () =>
  import('@/pages/partner-administration/partner-management/list/PartnerAdministrationList.vue');
const PartnerAdministrationEdit = () =>
  import('@/pages/partner-administration/partner-management/edit/PartnerAdministrationEdit.vue');
const PartnerAdministrationView = () =>
  import('@/pages/partner-administration/partner-management/view/PartnerAdministrationView.vue');
const PartnerAdministrationTokens = () =>
  import('@/pages/partner-administration/partner-management/tokens/PartnerAdministrationTokens.vue');
const PartnerIntegrationKnowledgeBase = () =>
  import('@/pages/partner-administration/partner-integration-knowledge-base/PartnerIntegrationKnowledgeBase.vue');
const PartnerUISettingsList = () =>
  import('@/pages/partner-administration/ui-settings/list/PartnerUISettingsList.vue');
const PartnerUISettingsCreate = () =>
  import('@/pages/partner-administration/ui-settings/create/PartnerUISettingsCreate.vue');
const PartnerUISettingsEdit = () =>
  import('@/pages/partner-administration/ui-settings/edit/PartnerUISettingsEdit.vue');
const RydInternalApiCredentialsManagement = () =>
  import('@/pages/partner-administration/partner-management/api-credentials/RydInternalApiCredentialsManagement.vue');
const KeyAccountManagerManagement = () =>
  import('@/pages/partner-administration/key-account-managers/KeyAccountManagerManagement.vue');
const AlertSlackChannelsPage = () =>
  import('@/pages/partner-administration/alert-slack-channels/AlertSlackChannelsPage.vue');
const PartnerOnboardingStatusAdmin = () =>
  import('@/pages/partner-administration/onboarding-status/PartnerOnboardingStatusAdmin.vue');
const PartnerDocumentsAdmin = () =>
  import('@/pages/partner-administration/partner-documents/PartnerDocumentsAdmin.vue');
const PartnerDocumentPreview = () =>
  import('@/pages/partner-administration/partner-documents/PartnerDocumentPreview.vue');

export const partnerAdministrationRoutes: AppRouteRecordRaw[] = [
  {
    path: '/partner-administration',
    component: Body,
    meta: {
      breadcrumb: 'Partner Administration',
    },
    children: [
      {
        // Navigating to the section root lands on the overview, similar to
        // /fleet-operations and other internal workspaces.
        path: '',
        redirect: { name: 'partnerAdministrationOverview' },
      },
      {
        path: 'overview',
        name: 'partnerAdministrationOverview',
        component: PartnerAdministrationOverview,
        meta: {
          title: 'Partner Administration Overview | ryd Portal',
          requiresAuth: true,
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'list',
        name: 'partnerList',
        component: PartnerAdministrationList,
        meta: {
          title: 'Partner Administration List | ryd Portal',
          breadcrumb: 'All Partners',
        },
      },
      {
        path: 'create',
        name: 'partnerCreate',
        component: PartnerAdministrationCreate,
        meta: {
          title: ' Partner Creation | ryd Portal',
          breadcrumb: 'Add Partner',
        },
      },
      {
        path: 'partner-integration-kb',
        name: 'partnerIntegrationKb',
        component: PartnerIntegrationKnowledgeBase,
        meta: {
          title: ' Partner Integration KB | ryd Portal',
          breadcrumb: 'Partner Integration KB',
        },
      },
      {
        path: 'view/:partnerConfigId',
        name: 'partnerAdministrationView',
        component: PartnerAdministrationView,
        meta: {
          title: 'Partner Details | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'Partner Details',
        },
      },
      {
        path: 'edit/:partnerConfigId',
        name: 'partnerAdministrationEdit',
        component: PartnerAdministrationEdit,
        meta: {
          title: ' Partner Edit | ryd Portal',
          breadcrumb: 'Edit Partner',
        },
      },
      {
        path: 'tokens/:partnerConfigId',
        name: 'partnerAdministrationTokensEdit',
        component: PartnerAdministrationTokens,
        meta: {
          title: ' Partner Tokens Edit | ryd Portal',
          breadcrumb: 'Manage Partner Tokens',
        },
      },
      {
        path: 'onboarding-status/:partnerConfigId',
        name: 'partnerOnboardingStatus',
        component: PartnerOnboardingStatusAdmin,
        meta: {
          title: 'Partner Onboarding Status | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'Onboarding Status',
        },
      },
      {
        path: 'api-credentials/:partnerConfigId',
        name: 'partnerApiCredentials',
        component: RydInternalApiCredentialsManagement,
        meta: {
          title: 'API Credentials Management | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['api_credentials_admin'],
          breadcrumb: 'API Credentials',
        },
      },
      {
        path: 'ui-settings/list',
        name: 'partnerUISettingsList',
        component: PartnerUISettingsList,
        meta: {
          title: 'UI Settings List | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'UI Settings',
        },
      },
      {
        path: 'ui-settings/create',
        name: 'partnerUISettingsCreate',
        component: PartnerUISettingsCreate,
        meta: {
          title: 'Create UI Settings | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'Create UI Settings',
        },
      },
      {
        path: 'ui-settings/edit',
        name: 'partnerUISettingsEdit',
        component: PartnerUISettingsEdit,
        meta: {
          title: 'Edit UI Settings | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'Edit UI Settings',
        },
      },
      {
        path: 'key-account-managers',
        name: 'keyAccountManagerManagement',
        component: KeyAccountManagerManagement,
        meta: {
          title: 'Key Account Manager Management | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['userManagementAdmin'],
          breadcrumb: 'Key Account Manager Management',
        },
      },
      {
        path: 'alert-slack-channels',
        name: 'partnerAlertSlackChannels',
        component: AlertSlackChannelsPage,
        meta: {
          title: 'Alert Slack Channels | ryd Portal',
          requiresAuth: true,
          requiredRoles: ['platform_communications_admin'],
          breadcrumb: 'Alert Slack Channels',
        },
      },
      {
        path: 'partner-documents',
        name: 'partnerDocumentsAdmin',
        component: PartnerDocumentsAdmin,
        meta: {
          title: 'Partner Documents | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          requiredRoles: ['partnerAdmin'],
          breadcrumb: 'Partner Documents',
        },
      },
      {
        // Full-page document preview for internal users. Deliberately not
        // gated on the partnerAdmin role: the backend grants read access to
        // every ryd-group user, and shared partner links are forwarded here.
        path: 'partner-documents/view/:documentId',
        name: 'partnerDocumentPreview',
        component: PartnerDocumentPreview,
        meta: {
          title: 'Document Preview | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Document Preview',
        },
      },
    ],
  },
];
